import {
  mergeOnboardingStateByNewest,
  readOnboardingStateFromStorage,
  writeOnboardingStateToStorage,
} from '~/onboarding/storage'
import type { OnboardingStateMap } from '~/onboarding/types'
import { defaultUserPreferences } from '~/utils/userPreferences/defaults'
import { fetchPrefsFromApi } from '~/utils/userPreferences/userPreferencesApi'
import {
  preferNewestLocalPrefs,
  readPrefsFromCookie,
  readPrefsFromLocalStorage,
  writePrefsToCookie,
} from '~/utils/userPreferences/userPreferencesStorage'
import type { ColorModePreference, UserPreferencesPayload } from '~/utils/userPreferences/types'

function mergeOnboardingFromAllSources(
  localPrefs: UserPreferencesPayload | null,
  serverPrefs: UserPreferencesPayload | null,
): OnboardingStateMap {
  const localOnboardingState = localPrefs?.onboarding?.state ?? {}
  const serverOnboardingState = serverPrefs?.onboarding?.state ?? {}
  const dedicatedLocalState = readOnboardingStateFromStorage()
  const mergedFromPrefs = mergeOnboardingStateByNewest(localOnboardingState, serverOnboardingState)
  return mergeOnboardingStateByNewest(dedicatedLocalState, mergedFromPrefs)
}

function resolveWelcomeSplashDone(
  localPrefs: UserPreferencesPayload | null,
  serverPrefs: UserPreferencesPayload | null,
): boolean {
  return localPrefs?.welcomeSplashDone === true || serverPrefs?.welcomeSplashDone === true
}

function preferNewest(
  localPrefs: UserPreferencesPayload | null,
  serverPrefs: UserPreferencesPayload | null,
): UserPreferencesPayload {
  if (localPrefs && serverPrefs) {
    return localPrefs.updatedAt >= serverPrefs.updatedAt ? localPrefs : serverPrefs
  }
  return localPrefs ?? serverPrefs ?? defaultUserPreferences()
}

function buildHydratedPreferences(
  localPrefs: UserPreferencesPayload | null,
  serverPrefs: UserPreferencesPayload | null,
): UserPreferencesPayload {
  const chosen = preferNewest(localPrefs, serverPrefs)
  const mergedOnboardingState = mergeOnboardingFromAllSources(localPrefs, serverPrefs)
  const hasOnboardingState = Object.keys(mergedOnboardingState).length > 0
  const welcomeSplashDone = resolveWelcomeSplashDone(localPrefs, serverPrefs)

  if (!hasOnboardingState) {
    return { ...chosen, welcomeSplashDone }
  }

  return {
    ...chosen,
    welcomeSplashDone,
    onboarding: {
      version: 1,
      updatedAt: Date.now(),
      state: mergedOnboardingState,
    },
  }
}

/**
 * Un enregistrement déclenché trop tôt (splash, thème) ne doit pas renvoyer
 * un onboarding plus vieux que celui déjà écrit dans le navigateur.
 */
export function withLatestOnboarding(nextPrefs: UserPreferencesPayload): UserPreferencesPayload {
  const mergedState = mergeOnboardingStateByNewest(
    nextPrefs.onboarding?.state ?? {},
    readOnboardingStateFromStorage(),
  )
  if (Object.keys(mergedState).length === 0) {
    return nextPrefs
  }
  return {
    ...nextPrefs,
    onboarding: {
      version: 1,
      updatedAt: Date.now(),
      state: mergedState,
    },
  }
}

type HydrateClientEffects = {
  readCookie: () => unknown
  writeCookie: (value: UserPreferencesPayload) => void
  applyColorMode: (preference: ColorModePreference) => void
}

export async function runHydrate(
  writeReady: (ready: boolean) => void,
  writeLoading: (loading: boolean) => void,
  applyPrefs: (nextPrefs: UserPreferencesPayload) => void,
  resolveAuth: () => { loggedIn: boolean; accessToken: string | undefined },
  clientEffects: HydrateClientEffects,
): Promise<void> {
  const { readCookie, writeCookie, applyColorMode } = clientEffects
  if (import.meta.server) {
    return
  }
  writeLoading(true)
  try {
    const cookiePrefs = readPrefsFromCookie(readCookie())
    const storagePrefs = readPrefsFromLocalStorage()
    const localPrefs = preferNewestLocalPrefs(cookiePrefs, storagePrefs)
    const { loggedIn, accessToken } = resolveAuth()
    const serverPrefs = loggedIn && accessToken ? await fetchPrefsFromApi() : null
    const hydrated = buildHydratedPreferences(localPrefs, serverPrefs)
    if (hydrated.onboarding?.state && Object.keys(hydrated.onboarding.state).length > 0) {
      writeOnboardingStateToStorage(hydrated.onboarding.state)
    }
    applyPrefs(hydrated)
    writePrefsToCookie(hydrated, writeCookie)
    applyColorMode(hydrated.colorModePreference)
    writeReady(true)
  } finally {
    writeLoading(false)
  }
}
